import { maybeWebContext } from "../search";
// chat/brain.ts — the chat brain, SERVER-FIRST since 2026-07-07 (the R8 graduation).
// askBrain now sends the message + this app's declared action catalog to the generic
// fleet chat brain (/api/apps/oriora_send_log/chat) — ONE billed call returns ONE
// registered action + a reply; the app executes it locally so data stays on-device.
// The regex intent-router below is the FREE FALLBACK (offline / signed-out / out of
// funds) — chat never goes dead. Original prototype notes:
//   • "add V4 at Movement flash 3x boulder"        → add_climb    (local parse, free)
//   • "mark V4 sent" / "V4 sent"                    → edit_climb   (local, free)
//   • "mark V4 project" / "project V4"              → edit_climb   (local, free)
//   • "V4 3 attempts" / "set V4 attempts 3"         → edit_climb   (local, free)
//   • "delete V4"                                   → delete_climb (HITL confirm)
//   • "show sends / attempts / projects / indoor / <venue>" → filter (local, free)
//   • "summarize / overview"                        → local stats  (local, free)
//   • "recap" / "season recap"                      → recapSeason  (billed call)
//   • "ask <question about your ticks>"             → askTicks      (billed call, fenced)
//   • coaching / grade / training / injury asks     → refused (the FENCE — no billed call)
//   • anything else                                 → none + a short nudge
//
// THE FENCE: Send Log only narrates the climber's logged PAST. isCoachingOrMedical() runs on
// the raw message BEFORE serverChat is ever called — a coaching/grade/training/injury ask is
// refused for FREE, so it never reaches the (billed) routing call either, not just the (billed)
// recap/ask calls one step later (which keep their own fences too, defence in depth).

import {
  store, ASCENTS, DISCIPLINES,
  type Climb, type NewClimb, type ClimbPatch, type Discipline, type Ascent,
} from "../store";
import { BrainError, isCoachingOrMedical, brainCall } from "../brain";
import type { ChatAction, FilterRow } from "./actions";
import { findNamedMatches, notFoundMessage } from "./fuzzy";
import { libraryFind, librarySummary } from "../library";

export interface ChatTurn {
  role: "user" | "assistant";
  content: string;
  rows?: FilterRow[]; // assistant turns may carry filter matches to render inline
}
export interface BrainResponse {
  reply: string;         // conversational preface; the executor supplies the result line
  actions: ChatAction[]; // the shared brain returns an ARRAY — two asks in one message = two actions (G17).
}
/** One resolved action (what toAction/routeMessage build); serverChat/askBrain assemble the array. */
interface One { reply: string; action: ChatAction }

// Sent to the shared chat brain as `scope` (defense-in-depth on top of the pre-LLM coaching/injury
// fence). Send Log is climbing/fitness-adjacent — keep the reply channel observational.
const SCOPE =
  "Hard limits: never coach, never suggest grades/projects/training plans, never give injury, medical, or " +
  "rehab advice — decline, and for injury point to a qualified professional. This app is a private climbing " +
  "tick log: add/edit/delete/search climbs and give an OBSERVATIONAL recap/answer about the climber's OWN " +
  "logged sends (hardest send, grade drift, venues, styles); back up to their own cloud.";

// The chat can't give coaching/grade/training/injury advice (mirrors brain.ts's own fence, which
// isn't exported as a string — we honor the exported isCoachingOrMedical() guard here).
const CHAT_FENCE_MESSAGE =
  "Send Log only narrates the climbs you've logged — it can't give coaching, grade, training, or injury advice. Try asking about your own ticks (\"ask what's my hardest send\") or use \"recap\" for a season write-up.";

const HELP =
  "I can log climbs, mark them sent, flag projects, set attempts, search, summarize, recap, and delete — just tell me. Try:\n" +
  '• "add V4 at Movement, boulder, flash"\n' +
  '• "mark V4 sent" · "project 5.11a" · "V4 3 attempts"\n' +
  '• "show sends" · "show indoor" · "summarize"\n' +
  '• "recap" · "ask what\'s my hardest send" · "delete V4"';

const none = (message: string): One => ({ reply: "", action: { kind: "none", message } });

// Forgiving climb lookup: an exact grade still wins, otherwise the FLEET fuzzy resolver (see ./fuzzy)
// tolerates misspellings, dropped words and word order over the climb's identifying text (grade +
// venue), with a note substring fallback. Climbs have no single name, so we build one for matching.
// Resolution stays entirely client-side (the log is on-device).
async function findClimb(term: string, filter?: (c: Climb) => boolean): Promise<Climb[]> {
  const q = term.trim().toLowerCase().replace(/[.?!]+$/, "");
  if (!q) return [];
  const all = await store.listClimbs({ limit: 50000 });
  const scoped = filter ? all.filter(filter) : all;
  const exact = scoped.filter((c) => c.grade.toLowerCase() === q);
  if (exact.length) return exact;
  const byName = findNamedMatches(scoped.map((c) => ({ ...c, name: [c.grade, c.venue].filter(Boolean).join(" ") })), term);
  if (byName.length) return byName;
  return scoped.filter((c) => c.note.toLowerCase().includes(q));
}

/** "Couldn't find a climb matching X" enriched with the closest climbs ("did you mean …?"). */
async function climbsNotFound(term: string): Promise<string> {
  const all = await store.listClimbs({ limit: 50000 });
  return notFoundMessage(all.map((c) => ({ name: climbLabel(c) })), term);
}

function climbLabel(c: Climb): string {
  const at = c.venue ? ` at ${c.venue}` : "";
  return `${c.grade || "climb"}${at} (${c.climbed_on})`;
}
function pickList(rows: Climb[]): string {
  return rows.slice(0, 5).map((c) => `• ${climbLabel(c)}`).join("\n");
}

function parseAdd(raw: string): NewClimb {
  const out: NewClimb = { grade: "" };
  let s = ` ${raw.trim()} `;

  // attempts: "3 attempts", "3 tries", "3 goes", "3x", "x3"
  const am = s.match(/\b(\d+)\s*(?:attempts?|tries|goes|x)\b/i) || s.match(/\bx\s*(\d+)\b/i);
  if (am) { out.attempts = Math.max(1, parseInt(am[1], 10)); s = s.replace(am[0], " "); }

  const venueParts: string[] = [];
  const leftover: string[] = [];
  let capturingVenue = false;
  for (const tok of s.trim().split(/\s+/)) {
    const w = tok.toLowerCase().replace(/[.,]+$/, "");
    if (!w) continue;
    if (/^(indoor|gym|inside)$/.test(w)) { out.environment = "indoor"; capturingVenue = false; continue; }
    if (/^(outdoor|outside|crag)$/.test(w)) { out.environment = "outdoor"; capturingVenue = false; continue; }
    if (/^(boulder|bouldering)$/.test(w)) { out.discipline = "boulder"; capturingVenue = false; continue; }
    if (/^sport$/.test(w)) { out.discipline = "sport"; capturingVenue = false; continue; }
    if (/^trad$/.test(w)) { out.discipline = "trad"; capturingVenue = false; continue; }
    if (/^(toprope|top-rope|tr)$/.test(w)) { out.discipline = "toprope"; capturingVenue = false; continue; }
    if (/^(flash|flashed)$/.test(w)) { out.ascent = "flash"; capturingVenue = false; continue; }
    if (/^(onsight|onsighted|os)$/.test(w)) { out.ascent = "onsight"; capturingVenue = false; continue; }
    if (/^(redpoint|redpointed|rp|sent|send)$/.test(w)) { out.ascent = "redpoint"; capturingVenue = false; continue; }
    if (/^(repeat|repeated)$/.test(w)) { out.ascent = "repeat"; capturingVenue = false; continue; }
    if (/^(attempt|attempted|tried|fell)$/.test(w)) { out.ascent = "attempt"; capturingVenue = false; continue; }
    if (/^(project|projected|proj)$/.test(w)) { out.project = true; capturingVenue = false; continue; }
    if (w === "at" || w === "@") { capturingVenue = true; continue; }
    if (capturingVenue) { venueParts.push(tok.replace(/[,]+$/, "")); continue; }
    leftover.push(tok.replace(/[,]+$/, ""));
  }
  if (venueParts.length) out.venue = venueParts.join(" ");
  out.grade = leftover.join(" ").trim();
  // Derive `sent` from the ascent when one was given (project alone stays an attempt).
  if (out.ascent) out.sent = out.ascent !== "attempt";
  return out;
}

export async function askBrain(history: ChatTurn[], onChunk?: (t: string) => void): Promise<BrainResponse> {
  const last = [...history].reverse().find((t) => t.role === "user");
  const text = (last?.content ?? "").trim();
  if (!text) { const r = await routeMessage(text); return { reply: r.reply, actions: [r.action] }; }
  // THE FENCE — pre-LLM, free, on BOTH paths. A coaching/grade/training/injury ask is refused
  // deterministically before it can reach EITHER billed call (routing OR recap/ask).
  if (isCoachingOrMedical(text)) { const r = none(CHAT_FENCE_MESSAGE); return { reply: r.reply, actions: [r.action] }; }
  try {
    return await serverChat(history, text, onChunk);
  } catch (e) {
    // A money/auth/key refusal must be SAID, not masked: the local fallback answering a
    // funds-blocked question as if it were a search reads as a broken app (found live 2026-08-27).
    if (e instanceof BrainError && (e.code === "insufficient_funds" || e.code === "unauthenticated" || e.code === "managed_ai_blocked")) {
      return { reply: e.message, actions: [{ kind: "none" }] };
    }
    // Offline / transient server trouble → the free local intent-router below
    // still answers; chat never goes dead. (Local path handles one intent → one action.)
    const r = await routeMessage(text);
    return { reply: r.reply, actions: [r.action] };
  }
}

// ---- SERVER CHAT-BRAIN (the graduated path) --------------------------------
// The app declares WHAT it can do; the fleet chat brain decides WHICH action the
// message means; this file resolves references (grade/venue text → id) locally,
// because the data lives here.

// Standing instructions: the customer shapes the app through chat; stored on-device, sent every call.
const INSTRUCTIONS_KEY = "oriora.send-log.instructions";
export const getInstructions = (): string => { try { return localStorage.getItem(INSTRUCTIONS_KEY) ?? ""; } catch { return ""; } };
export const addInstruction = (line: string): void => { try { const cur = getInstructions(); localStorage.setItem(INSTRUCTIONS_KEY, cur ? cur + "\n- " + line : "- " + line); } catch { /* ignore */ } };

export const CHAT_ACTIONS = [
  { name: "add_climb", description: "Log a completed or attempted climb.", params: { grade: "REQUIRED grade as the user wrote it (e.g. V4, 5.11a, 6c+)", venue: "optional gym or crag name", environment: '"indoor" or "outdoor"', discipline: '"boulder", "sport", "trad", "toprope", or "other"', ascent: '"flash", "onsight", "redpoint", "repeat", or "attempt"', attempts: "optional number of tries/sessions", project: "optional true if this is an ongoing project (not yet sent)", note: "optional free note" } },
  { name: "edit_climb", description: "Change an existing logged climb — mark it sent, flag/unflag as a project, set attempts, or update venue/note. Identify it by grade (and venue if there are duplicates).", params: { grade: "REQUIRED — which climb, grade text as the user wrote it", sent: "true to mark it sent (topped out / clipped chains)", project: "true to flag as an ongoing project, false to clear the flag", attempts: "new attempts/tries count, only if changing", venue: "new venue, only if changing", note: "new note, only if changing" } },
  { name: "delete_climb", description: "Delete one logged climb identified by its grade (the app asks the user to confirm first).", params: { grade: "which climb — grade text as the user wrote it" } },
  { name: "filter", description: "Search / list the user's logged climbs.", params: { status: 'optional "sends", "attempts" (not-yet-sent), or "projects"', environment: 'optional "indoor" or "outdoor"', discipline: 'optional "boulder", "sport", "trad", "toprope", or "other"', ascent: 'optional "flash", "onsight", "redpoint", "repeat", or "attempt"', text: "optional free text to search grade/venue/note/style" } },
  { name: "recap", description: "Narrate the season: hardest send, attempts-before-tick, grade drift, indoor/outdoor split, ascent/style/discipline mixes, top venues/partners, most-worked projects — computed on-device, observational only. Never coaches, trains, or gives injury/medical advice. No params.", params: {} },
  { name: "ask", description: "Answer a descriptive question about the user's OWN logged climbs (e.g. hardest send, attempts before a tick, indoor vs outdoor split). Never coaching, grade-progression, training, or injury/medical advice.", params: { question: "REQUIRED — the user's question, as asked" } },
  { name: "summarize", description: "Quick corpus stats: total climbs, sends, attempts-only, days out, hardest grade, sends this year — computed on-device. No params.", params: {} },
  { name: "backup_save", description: "Back up the WHOLE collection to the customer's own GitHub or Google Drive (connected once in their Oriora account). Works for ANY size — if the backup is too large for the standard path (climb photos), it saves straight from this device to their GitHub when that's logged in (backup menu), or the reply tells them the one-time login to enable it.", params: {} },
  { name: "backup_status", description: "Tell the customer WHERE their data is backed up: which cloud is connected in their Oriora account, which one new backups write to, and when the last backup was. Use for 'where is my data / is my stuff backed up / which cloud do you use'.", params: {} },
  { name: "backup_restore", description: "Restore from the customer's cloud backup — REPLACES current data (the app asks the user to confirm first). If they NAME a source ('restore from Drive'), pass provider.", params: { provider: "optional github|gdrive when the customer names which cloud to restore from" } },
  { name: "save_local_copy", description: "Save a one-off copy of the user's data to a folder/location they pick on THIS device (the OS picker). Works on iPhone and Mac. Use for 'save my data to a file' / 'save a copy locally'.", params: {} },
  { name: "import_file", description: "Import / restore the user's data from a FILE they pick on this device — Files, iCloud, a folder, or a backup from another device. REPLACES current data (the app asks them to confirm first). Use for 'import my data', 'restore from a file', 'load my backup file'.", params: {} },
  { name: "save_to_cloud", description: "Save a NOTE or THIS conversation to the customer's OWN GitHub or Google Drive. Use when they ask to store/save/keep the conversation, their messages, or a piece of text. (To back up the WHOLE climb log instead, use backup_save.)", params: { content_kind: "\"conversation\" to save this whole chat, or \"note\" for a piece of text", note: "the text to save when content_kind is \"note\"", destination: "optional \"github\" or \"gdrive\" if the user names one; omit otherwise" } },
  { name: "remember", description: "Save a standing instruction for how THIS customer wants the app to behave from now on.", params: { instruction: "the preference, one sentence" } },
  // ── ARTIFACT TOOLS — the customer's own library (device-only). Whatever the AI
  // makes for them gets KEPT here instead of vanishing into the chat. Fleet-wide
  // capability, identical in every Oriora app.
  { name: "library_save", description: "Save a plan, summary, guide or note into the customer's ON-DEVICE library. Use whenever you produce something worth keeping, or they ask you to save/remember/write something down. When they ask you to RESEARCH something and make them a plan: write the FULL text in \`content\`, give it a clear title, and put a short summary in your reply.", params: { title: "short title", item_kind: "lesson | plan | note", content: "the FULL text to save — write it out completely", folder: "optional folder name" } },
  { name: "library_list", description: "List what the customer has saved in their library ('what have I saved', 'show my plans', 'what's in my library / in folder X').", params: { folder: "optional folder to filter by" } },
  { name: "library_remove", description: "Remove ONE library item by its title (the app asks the customer to confirm first). LIBRARY items only — the app's own entries have their own delete action.", params: { title: "which item (its title, from LIBRARY in the context)" } },
  { name: "library_move", description: "Move a library item into a folder ('move the summer plan into Plans').", params: { title: "which item", folder: "target folder name" } },
  { name: "library_merge", description: "Merge two or more library items into one (the app asks the customer to confirm first).", params: { titles: "the titles to merge, separated by |", new_title: "title for the merged item" } },
];

interface ServerAct { name?: string; action?: string; params?: Record<string, unknown> }
interface ServerChat { actions?: ServerAct[]; action?: string; params?: Record<string, unknown>; reply?: string }

async function serverChat(history: ChatTurn[], text: string, onChunk?: (t: string) => void): Promise<BrainResponse> {
  const climbs = await store.listClimbs({ limit: 40 });
  const grades = [...new Set(climbs.map((c) => c.grade).filter(Boolean))].slice(0, 30);
  const venues = [...new Set(climbs.map((c) => c.venue).filter(Boolean))].slice(0, 20);
  const _notes = getInstructions();
  const context = (`LIBRARY (their saved plans/notes): ${librarySummary()}. Recent grades logged: ${grades.join(", ") || "(none yet)"}. Venues: ${venues.join(", ") || "(none yet)"}.`)
    + (_notes ? "\nCustomer's standing instructions (apply when filling params): " + _notes : "");
  const recent = history.slice(-21, -1).map((t) => ({ role: t.role, content: t.content }));
  const r = await brainCall<ServerChat>("chat", { message: text + (await maybeWebContext(text)), actions: CHAT_ACTIONS, context, history: recent, scope: SCOPE }, onChunk);
  const say = asStr(r.reply) ?? "";
  const raw: ServerAct[] = Array.isArray(r.actions) && r.actions.length
    ? r.actions
    : (r.action ? [{ action: r.action, params: r.params }] : []);   // back-compat / older server
  if (!raw.length) return { reply: say || HELP, actions: [{ kind: "none" }] };
  const actions: ChatAction[] = [];
  for (const a of raw) {
    const one = await toAction({ action: a.name ?? a.action ?? "", params: a.params, reply: "" }, text);
    actions.push(one.action);
  }
  return { reply: say, actions };
}

const asStr = (v: unknown): string | undefined => (typeof v === "string" && v.trim() ? v.trim() : undefined);
const asBool = (v: unknown): boolean | undefined => (typeof v === "boolean" ? v : undefined);
const asInt = (v: unknown, lo: number, hi: number): number | undefined => {
  const n = typeof v === "number" ? v : parseFloat(String(v ?? ""));
  return Number.isFinite(n) && n >= lo && n <= hi ? Math.round(n) : undefined;
};

export async function toAction(r: ServerChat, userText: string): Promise<One> {
  const p = r.params ?? {};
  const say = asStr(r.reply) ?? "";

  // ── ARTIFACT TOOLS (the library) — handled EARLY, above any app-specific
  // precondition (no vehicle / no plant / nothing selected). The library belongs
  // to the customer, not to a currently-active record. (Garage shipped this bug:
  // its no-vehicle guard silently swallowed every library action, 2026-08-13.)
  if (r.action === "library_save") {
    const _content = asStr(p.content);
    if (!_content) return none("Tell me what to save — or ask me to write it first and I'll keep it.");
    const _rawKind = (asStr(p.item_kind) ?? "note").toLowerCase();
    const _itemKind = _rawKind === "lesson" || _rawKind === "plan" ? _rawKind : "note";
    return { reply: say, action: { kind: "library_save", title: asStr(p.title) ?? "Untitled", itemKind: _itemKind, content: _content, folder: asStr(p.folder) } };
  }
  if (r.action === "library_list") return { reply: say, action: { kind: "library_list", folder: asStr(p.folder) } };
  if (r.action === "library_remove") {
    const _r = resolveLibraryItem(asStr(p.title));
    if (!_r.item) return none(_r.problem!);
    return { reply: say, action: { kind: "library_remove", id: _r.item.id, label: `"${_r.item.title}"` } };
  }
  if (r.action === "library_move") {
    const _folder = asStr(p.folder);
    if (!_folder) return none("Which folder should it go to?");
    const _r2 = resolveLibraryItem(asStr(p.title));
    if (!_r2.item) return none(_r2.problem!);
    return { reply: say, action: { kind: "library_move", id: _r2.item.id, folder: _folder, label: `"${_r2.item.title}"` } };
  }
  if (r.action === "library_merge") {
    const _titles = (asStr(p.titles) ?? "").split("|").map((s) => s.trim()).filter(Boolean);
    if (_titles.length < 2) return none("Tell me which two (or more) items to merge, by title.");
    const _ids: string[] = [];
    for (const _t of _titles) {
      const _r3 = resolveLibraryItem(_t);
      if (!_r3.item) return none(_r3.problem!);
      _ids.push(_r3.item.id);
    }
    return { reply: say, action: { kind: "library_merge", ids: _ids, title: asStr(p.new_title) ?? _titles.join(" + "), label: _titles.map((t) => `"${t}"`).join(" + ") } };
  }
  switch (r.action) {
    case "add_climb": {
      const grade = asStr(p.grade);
      if (!grade) return none(say || 'What grade was it? Try "add V4 at Movement, boulder, flash".');
      const input: NewClimb = { grade };
      const venue = asStr(p.venue);
      if (venue) input.venue = venue;
      const environment = asStr(p.environment)?.toLowerCase();
      if (environment === "indoor" || environment === "outdoor") input.environment = environment;
      const discipline = asStr(p.discipline)?.toLowerCase();
      if (discipline && (DISCIPLINES as string[]).includes(discipline)) input.discipline = discipline as Discipline;
      const ascent = asStr(p.ascent)?.toLowerCase();
      if (ascent && (ASCENTS as string[]).includes(ascent)) {
        input.ascent = ascent as Ascent;
        input.sent = ascent !== "attempt";
      }
      const attempts = asInt(p.attempts, 1, 999);
      if (attempts != null) input.attempts = attempts;
      const project = asBool(p.project);
      if (project != null) input.project = project;
      const note = asStr(p.note);
      if (note) input.note = note;
      return { reply: say, action: { kind: "add_climb", input } };
    }
    case "edit_climb": {
      const term = asStr(p.grade);
      if (!term) return none(say || "Which climb do you want to edit? Give me its grade or venue.");
      const patch: ClimbPatch = {};
      const bits: string[] = [];
      let scopeFilter: ((c: Climb) => boolean) | undefined;
      const sentFlag = asBool(p.sent);
      if (sentFlag === true) { patch.sent = true; bits.push("sent"); scopeFilter = (c) => !c.sent; }
      const projectFlag = asBool(p.project);
      if (projectFlag === true) { patch.project = true; bits.push("project"); scopeFilter = scopeFilter ?? ((c) => !c.project); }
      else if (projectFlag === false) { patch.project = false; bits.push("no longer project"); }
      const attempts = asInt(p.attempts, 1, 999);
      if (attempts != null) { patch.attempts = attempts; bits.push(`${attempts} attempt${attempts !== 1 ? "s" : ""}`); }
      const venue = asStr(p.venue);
      if (venue) { patch.venue = venue; bits.push(`at ${venue}`); }
      const note = asStr(p.note);
      if (note) { patch.note = note; bits.push("note"); }
      if (Object.keys(patch).length === 0) return none(say || "What should I change — sent, project, attempts, venue, or a note?");
      const rows = await findClimb(term, scopeFilter);
      if (rows.length === 0) return none(await climbsNotFound(term));
      if (rows.length > 1) return none(`I found ${rows.length} matching "${term}" — be more specific:\n${pickList(rows)}`);
      return { reply: say, action: { kind: "edit_climb", id: rows[0].id, patch, label: `${climbLabel(rows[0])} → ${bits.join(" · ")}` } };
    }
    case "delete_climb": {
      const term = asStr(p.grade);
      if (!term) return none(say || "Which climb do you want to delete? Give me its grade.");
      const rows = await findClimb(term);
      if (rows.length === 0) return none(await climbsNotFound(term));
      if (rows.length > 1) return none(`I found ${rows.length} matching "${term}" — be more specific:\n${pickList(rows)}`);
      return { reply: say, action: { kind: "delete_climb", id: rows[0].id, label: climbLabel(rows[0]) } };
    }
    case "filter": {
      const status = asStr(p.status)?.toLowerCase();
      const environment = asStr(p.environment)?.toLowerCase();
      const discipline = asStr(p.discipline)?.toLowerCase();
      const ascent = asStr(p.ascent)?.toLowerCase();
      const text = asStr(p.text)?.toLowerCase();
      const preds: ((c: Climb) => boolean)[] = [];
      if (status === "projects" || status === "project") preds.push((c) => c.project);
      else if (status === "sends" || status === "sent") preds.push((c) => c.sent);
      else if (status === "attempts" || status === "unsent" || status === "attempts only") preds.push((c) => !c.sent);
      if (environment === "indoor" || environment === "outdoor") preds.push((c) => c.environment === environment);
      if (discipline && (DISCIPLINES as string[]).includes(discipline)) preds.push((c) => c.discipline === discipline);
      if (ascent && (ASCENTS as string[]).includes(ascent)) preds.push((c) => c.ascent === ascent);
      if (text) {
        preds.push((c) =>
          c.grade.toLowerCase().includes(text) ||
          c.venue.toLowerCase().includes(text) ||
          c.note.toLowerCase().includes(text) ||
          c.style.some((s) => s.toLowerCase().includes(text)));
      }
      const pred = (c: Climb): boolean => preds.every((f) => f(c));
      return { reply: "", action: { kind: "filter", pred, label: say || userText } };
    }
    case "recap":
      return { reply: say, action: { kind: "recap" } };
    case "ask": {
      const question = asStr(p.question);
      if (!question) return none(say || "Ask me something about your logged climbs — e.g. \"what's my hardest send\".");
      if (isCoachingOrMedical(question)) return none(CHAT_FENCE_MESSAGE);
      return { reply: say, action: { kind: "ask", question } };
    }
    case "summarize":
      return { reply: say, action: { kind: "summarize" } };
    case "backup_save":
      return { reply: say, action: { kind: "backup_save" } };
    case "backup_status":
      return { reply: say, action: { kind: "backup_status" } };
    case "backup_restore": {
      const prov = asStr(p.provider);
      return { reply: say, action: { kind: "backup_restore", provider: prov === "github" || prov === "gdrive" ? prov : undefined } };
    }
    case "save_local_copy":
      return { reply: say, action: { kind: "save_local_copy" } };
    case "import_file":
      return { reply: say, action: { kind: "import_file" } };
    case "save_to_cloud": {
      const ck = asStr(p.content_kind) ?? asStr(p.contentKind) ?? "";
      const contentKind = /conversation|chat|messages|prompts/i.test(ck) ? "conversation" : "note";
      const dest = asStr(p.destination);
      const destination = dest === "github" ? "github" : dest === "gdrive" ? "gdrive" : undefined;
      return { reply: say, action: { kind: "save_to_cloud", contentKind, note: asStr(p.note) ?? asStr(p.content), destination } };
    }
    case "remember": {
      const instruction = asStr(p.instruction);
      if (!instruction) return none(say || "What should I remember?");
      return { reply: say, action: { kind: "remember", instruction } };
    }
    default:
      return none(say || HELP);
  }
}

export async function routeMessage(text: string): Promise<One> {
  const t = text.trim();
  if (!t) return { reply: "", action: { kind: "none", message: HELP } };
  const low = t.toLowerCase();

  // 1) SUMMARIZE — local corpus stats (free). Checked before the fence so "how am I doing"
  //    resolves to free local stats rather than tripping the coaching guard.
  if (/\b(summar(?:y|ise|ize)|overview|how\s+am\s+i\s+doing)\b/i.test(low)) {
    return { reply: "", action: { kind: "summarize" } };
  }

  // 2) RECAP — the season recap (billed). No target needed; it narrates the whole season.
  if (/^(?:recap|season\s+recap|recap\s+(?:my\s+)?season)\b/i.test(low)) {
    return { reply: "", action: { kind: "recap" } };
  }

  // 3) ASK <question> — explicit free-text question about the ticks (billed). The FENCE runs
  //    first: coaching/grade/training/injury asks are refused with NO billed call.
  let m = t.match(/^ask\s+(?:me\s+)?(.+)$/i);
  if (m) {
    const q = m[1].trim();
    if (!q) return { reply: "", action: { kind: "none", message: "Ask me something about your logged climbs — e.g. \"ask what's my hardest send\"." } };
    if (isCoachingOrMedical(q)) return { reply: "", action: { kind: "none", message: CHAT_FENCE_MESSAGE } };
    return { reply: "", action: { kind: "ask", question: q } };
  }

  // 0) FENCE — any other coaching/medical phrasing is refused before it can reach a billed call.
  if (isCoachingOrMedical(t)) return { reply: "", action: { kind: "none", message: CHAT_FENCE_MESSAGE } };

  // 4) DELETE — resolve locally; the UI asks for confirmation before executing.
  m = t.match(/^(?:delete|remove)\s+(?:the\s+)?(?:climb\s+)?(.+)$/i);
  if (m) {
    const rows = await findClimb(m[1]);
    if (rows.length === 0) return { reply: "", action: { kind: "none", message: await climbsNotFound(m[1]) } };
    if (rows.length > 1) return { reply: "", action: { kind: "none", message: `I found ${rows.length} matching "${m[1].trim()}" — be more specific:\n${pickList(rows)}` } };
    return { reply: "", action: { kind: "delete_climb", id: rows[0].id, label: climbLabel(rows[0]) } };
  }

  // 5) MARK SENT — flip an attempt to a send (only affects not-yet-sent climbs).
  m = t.match(/^(?:mark|set)\s+(.+?)\s+(?:as\s+)?(?:sent|topped(?:\s*out)?|done)\s*$/i)
    || t.match(/^(?:sent|topped(?:\s*out)?)\s+(.+)$/i)
    || t.match(/^(.+?)\s+(?:is\s+)?(?:sent|topped\s*out|done)\s*$/i);
  if (m) {
    const rows = await findClimb(m[1], (c) => !c.sent);
    if (rows.length === 0) return { reply: "", action: { kind: "none", message: `I couldn't find an un-sent climb matching "${m[1].trim()}".` } };
    if (rows.length > 1) return { reply: "", action: { kind: "none", message: `I found ${rows.length} un-sent matching "${m[1].trim()}" — be more specific:\n${pickList(rows)}` } };
    return { reply: "", action: { kind: "edit_climb", id: rows[0].id, patch: { sent: true }, label: `${climbLabel(rows[0])} → sent` } };
  }

  // 6) MARK PROJECT — flag a line as an ongoing project.
  m = t.match(/^(?:mark|set)\s+(.+?)\s+(?:as\s+)?(?:a\s+)?project\s*$/i)
    || t.match(/^project\s+(.+)$/i);
  if (m) {
    const rows = await findClimb(m[1], (c) => !c.project);
    if (rows.length === 0) return { reply: "", action: { kind: "none", message: `I couldn't find a non-project climb matching "${m[1].trim()}".` } };
    if (rows.length > 1) return { reply: "", action: { kind: "none", message: `I found ${rows.length} matching "${m[1].trim()}" — be more specific:\n${pickList(rows)}` } };
    return { reply: "", action: { kind: "edit_climb", id: rows[0].id, patch: { project: true }, label: `${climbLabel(rows[0])} → project` } };
  }

  // 7) SET ATTEMPTS — "V4 3 attempts" / "set V4 attempts 3".
  m = t.match(/^(?:set\s+)?(.+?)\s+(\d+)\s*(?:attempts?|tries|goes)\s*$/i)
    || t.match(/^(?:set\s+)?(.+?)\s+attempts?\s+(\d+)\s*$/i);
  if (m) {
    const attempts = Math.max(1, parseInt(m[2], 10));
    const rows = await findClimb(m[1]);
    if (rows.length === 0) return { reply: "", action: { kind: "none", message: await climbsNotFound(m[1]) } };
    if (rows.length > 1) return { reply: "", action: { kind: "none", message: `I found ${rows.length} matching "${m[1].trim()}" — be more specific:\n${pickList(rows)}` } };
    return { reply: "", action: { kind: "edit_climb", id: rows[0].id, patch: { attempts }, label: `${climbLabel(rows[0])} → ${attempts} attempt${attempts !== 1 ? "s" : ""}` } };
  }

  // 8) ADD — light local parse (no server call).
  m = t.match(/^(?:add|log|new|track|climbed|did)\s+(?:a\s+|an\s+)?(?:climb\s+)?(.+)$/i);
  if (m) {
    const input = parseAdd(m[1]);
    if (!input.grade) return { reply: "", action: { kind: "none", message: "What grade was it? Try \"add V4 at Movement, boulder, flash\"." } };
    return { reply: "", action: { kind: "add_climb", input } };
  }

  // 9) FILTER / SEARCH — local predicate over the tick list.
  const filter = buildFilter(t, low);
  if (filter) return { reply: "", action: { kind: "filter", pred: filter.pred, label: filter.label } };

  // 10) Unclear → a short nudge.
  return { reply: "", action: { kind: "none", message: HELP } };
}

function buildFilter(t: string, low: string): { pred: (c: Climb) => boolean; label: string } | null {
  if (/\bprojects?\b/i.test(low)) return { pred: (c) => c.project, label: "projects" };
  if (/\b(sends?|sent|topped\s*out|ticks?)\b/i.test(low)) return { pred: (c) => c.sent, label: "sends" };
  if (/\b(attempts?\s*only|didn'?t\s*send|un-?sent|failed)\b/i.test(low)) return { pred: (c) => !c.sent, label: "attempts" };
  if (/\b(indoor|gym|inside)\b/i.test(low)) return { pred: (c) => c.environment === "indoor", label: "indoor" };
  if (/\b(outdoor|outside|crag)\b/i.test(low)) return { pred: (c) => c.environment === "outdoor", label: "outdoor" };
  for (const d of DISCIPLINES) {
    if (new RegExp(`\\b${d}\\b`, "i").test(low)) return { pred: (c) => c.discipline === d, label: d };
  }
  for (const a of ASCENTS) {
    if (new RegExp(`\\b${a}(?:ed)?\\b`, "i").test(low)) return { pred: (c) => c.ascent === a, label: a };
  }
  // "show/find <term>" → match by grade, venue, style, or note.
  const m = t.match(/^(?:show|find|list|search)\s+(?:me\s+)?(?:all\s+)?(?:my\s+)?(.+)$/i);
  if (m) {
    const q = m[1].trim().toLowerCase().replace(/[.?!]+$/, "");
    if (!q) return null;
    return {
      pred: (c) =>
        c.grade.toLowerCase().includes(q) ||
        c.venue.toLowerCase().includes(q) ||
        c.note.toLowerCase().includes(q) ||
        c.style.some((s) => s.toLowerCase().includes(q)),
      label: q,
    };
  }
  return null;
}

// Resolve a library title to exactly one item, or explain why not (same 0/1/many
// contract the app uses for its own entries).
function resolveLibraryItem(term: string | undefined): { item?: import("../library").LibraryItem; problem?: string } {
  if (!term) return { problem: "Which saved item do you mean? Give me its title." };
  const found = libraryFind(term);
  if (found.length === 0) return { problem: `I couldn't find "${term}" in your library.` };
  if (found.length > 1) return { problem: `I found ${found.length} matching "${term}" — be more specific: ${found.map((f) => `"${f.title}"`).join(", ")}` };
  return { item: found[0] };
}
